/**
 * SiteConfiguration - Site and location settings component
 *
 * Handles site name, address, location, facility type, and utility information.
 *
 * Supports two modes:
 * 1. Props mode (legacy): Pass individual values and setters as props
 * 2. Context mode (new): Uses React Hook Form context when available
 *
 * The component automatically detects which mode to use based on whether
 * it's wrapped in a FormProvider and whether props are provided.
 */

import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { useFormField } from './useFormField';

const FACILITY_TYPES = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'commercial_office', label: 'Commercial Office' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'cold_storage', label: 'Cold Storage' },
  { value: 'data_center', label: 'Data Center' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'ev_charging', label: 'EV Charging Facility' },
  { value: 'mixed_use', label: 'Mixed Use' },
];

const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];


/**
 * SiteConfiguration Component
 *
 * @param {Object} props - Component props
 * @param {Object} [props.vortxStyles] - Custom styles
 * @param {boolean} [props.showRequired] - Show required field indicators
 * @param {Array} [props.missingFields] - List of missing required fields
 */
function SiteConfiguration({
  // Props mode values (backward compatible)
  siteName: propSiteName,
  setSiteName,
  street: propStreet,
  setStreet,
  city: propCity,
  setCity,
  state: propState,
  setState,
  zipCode: propZipCode,
  setZipCode,
  siteDescription: propSiteDescription,
  setSiteDescription,
  facilityType: propFacilityType,
  setFacilityType,
  utilityName: propUtilityName,
  setUtilityName,
  // Common props
  onZipCodeChange,
  vortxStyles = {},
  showRequired = false,
  missingFields = [],
}) {
  // Get form fields (automatically uses context or props)
  const siteName = useFormField('site.siteName', propSiteName, setSiteName);
  const street = useFormField('site.street', propStreet, setStreet);
  const city = useFormField('site.city', propCity, setCity);
  const state = useFormField('site.state', propState, setState);
  const zipCode = useFormField('site.zipCode', propZipCode, setZipCode);
  const siteDescription = useFormField('site.siteDescription', propSiteDescription, setSiteDescription);
  const facilityType = useFormField('site.facilityType', propFacilityType, setFacilityType);
  const utilityName = useFormField('site.utilityName', propUtilityName, setUtilityName);

  const isFieldMissing = useCallback((fieldKey) => {
    return showRequired && missingFields.some(f => f.key === fieldKey);
  }, [showRequired, missingFields]);

  const handleZipCodeBlur = useCallback(() => {
    if (zipCode.value && zipCode.value.length === 5 && onZipCodeChange) {
      onZipCodeChange(zipCode.value);
    }
  }, [zipCode.value, onZipCodeChange]);

  return (
    <Card sx={{ ...vortxStyles.card, mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOn color="primary" />
          <Typography
            variant="h6"
            sx={{
              fontFamily: vortxTypography.heading,
              color: 'text.primary',
              ml: 1,
              fontWeight: 600,
            }}
          >
            Site Information
          </Typography>
        </Box>

        {/* Site Name & Facility Type Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Site Name"
            value={siteName.value}
            onChange={(e) => siteName.setValue(e.target.value)}
            fullWidth
            placeholder="e.g., Main Distribution Center"
            sx={vortxStyles.textField}
            error={!!siteName.error}
            helperText={siteName.error?.message}
          />
          <TextField
            select
            label="Facility Type"
            value={facilityType.value || 'commercial'}
            onChange={(e) => facilityType.setValue(e.target.value)}
            sx={{ ...vortxStyles.textField, minWidth: 200 }}
            error={!!facilityType.error}
            helperText={facilityType.error?.message}
          >
            {FACILITY_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Street Address */}
        <TextField
          label="Street Address"
          value={street.value}
          onChange={(e) => street.setValue(e.target.value)}
          fullWidth
          placeholder="123 Main Street"
          sx={{ ...vortxStyles.textField, mb: 2 }}
          error={!!street.error}
          helperText={street.error?.message}
        />

        {/* City, State, Zip Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="City"
            value={city.value}
            onChange={(e) => city.setValue(e.target.value)}
            placeholder="Los Angeles"
            sx={{ ...vortxStyles.textField, flex: 2 }}
            error={!!city.error}
            helperText={city.error?.message}
          />
          <TextField
            select
            label="State"
            value={state.value || ''}
            onChange={(e) => state.setValue(e.target.value)}
            sx={{ ...vortxStyles.textField, minWidth: 150 }}
            error={!!state.error}
            helperText={state.error?.message}
          >
            {US_STATES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={`Zip Code${isFieldMissing('zipCode') ? ' *' : ''}`}
            value={zipCode.value}
            onChange={(e) => zipCode.setValue(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onBlur={handleZipCodeBlur}
            error={isFieldMissing('zipCode') || !!zipCode.error}
            sx={{ ...vortxStyles.textField, width: 130 }}
            inputProps={{ maxLength: 5 }}
            InputLabelProps={{ shrink: true }}
            helperText={zipCode.error?.message}
          />
        </Box>

        {isFieldMissing('zipCode') && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Zip code is required for tariff lookup and solar resource data
          </Alert>
        )}

        {/* Description */}
        <TextField
          label="Site Description (optional)"
          value={siteDescription.value}
          onChange={(e) => siteDescription.setValue(e.target.value)}
          fullWidth
          multiline
          rows={2}
          placeholder="Additional notes about this site..."
          sx={{ ...vortxStyles.textField, mb: 2 }}
          error={!!siteDescription.error}
          helperText={siteDescription.error?.message}
        />

        {/* Utility Name */}
        <TextField
          label="Utility Name"
          value={utilityName.value}
          onChange={(e) => utilityName.setValue(e.target.value)}
          fullWidth
          placeholder="e.g., Pacific Gas & Electric"
          sx={vortxStyles.textField}
          error={!!utilityName.error}
          helperText={utilityName.error?.message}
        />
      </CardContent>
    </Card>
  );
}

// Export constants for use elsewhere
export { FACILITY_TYPES, US_STATES };

export default SiteConfiguration;
